import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AuthService } from '../../services/auth.service';
import { KycService } from '../../services/kyc.service';
import Swal from 'sweetalert2';
import { KycService as ModalKycSrv } from '../../services/modal-kyc/kyc.service';
import { Router } from '@angular/router';
import { externalInvestForms } from '../../../environments/environment';
import { ProjectStatus } from '../../shared/models/project-status.enum';
import { FavoritesService } from '../../services/favorites.service';
import { Subscription } from 'rxjs';

declare var $: any;

export interface EncryptedData {
  userEmail: string;
  projectName: string;
  tokenQuantity: number;
  tokenAddress: string;
  sellerAddress: string;
  uid: string;
  wallet: string;
}

@Component({
  selector: 'app-card-real-estate',
  templateUrl: './card-real-estate.component.html',
  styleUrls: ['./card-real-estate.component.css'],
})
export class CardRealEstateComponent implements OnInit, OnDestroy {
  @Input() info: any | undefined;
  @Input() pushLeft: boolean = false;
  @Input() userInversor: any | undefined;
  @Input() userWallets: any;
  @Input() loadingButtons: boolean = true;
  @ViewChild('modalContent') modalContent: TemplateRef<any> | undefined;

  userId: string | null = null;
  private subs: Subscription = new Subscription();

  logged: boolean = false;
  nowTimeStamp: number = Math.floor(Date.now() / 1000);
  get eurosSold(): number {
    return (Math.max(0, this.info?.reserves ?? 0) + (this.info?.amountSold ?? 0)) * ((this.info?.priceToken ?? 0) / 100);
  }

  get percentageSoldNumber(): number {
    if (this.info?.amountToSell && this.info?.priceToken) {
      return (this.eurosSold * 100) / (this.info.amountToSell * (this.info.priceToken / 100));
    }
    return 0;
  }

  get percentageSold(): string {
    return this.percentageSoldNumber.toFixed(2);
  }

  /** Bono de venta privada: 0,5% de rentabilidad sobre el periodo total (0,005 en tanto por uno). */
  private readonly PRIVATE_SALE_BONUS = 0.005;

  get hasPrivateSaleBonus(): boolean {
    return (this.info?.percentageToPrivateSale ?? 0) > 0;
  }

  /** Convierte un valor numérico o string (acepta coma decimal) a number; 0 si no es válido. */
  private toNumber(value: any): number {
    if (typeof value === 'number') {
      return isNaN(value) ? 0 : value;
    }
    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(',', '.'));
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  /**
   * TIR de venta privada = TIR general + ((1 + 0,005)^(1 / (Duración estimada * 30 / 365)) - 1)
   * El bono es un 0,5% sobre el periodo total; la fórmula lo anualiza.
   * La duración estimada está en meses; *30/365 la convierte en fracción de año.
   * El bono anualizado es un decimal, se multiplica por 100 para sumarlo al annualReturn (porcentaje).
   * Acepta duraciones con decimales (incluso en formato string con coma).
   */
  get privateSaleReturn(): number {
    const annualReturn = this.toNumber(this.info?.annualReturn);
    const months = this.toNumber(this.info?.estimatedDeliveryTime);
    if (months <= 0) {
      return annualReturn;
    }
    const years = (months * 30) / 365;
    const bonus = (Math.pow(1 + this.PRIVATE_SALE_BONUS, 1 / years) - 1) * 100;
    return annualReturn + bonus;
  }

  get privateSaleReturnFormatted(): string {
    return this.privateSaleReturn.toFixed(2).replace('.', ',');
  }

  get privateSaleTooltipText(): string {
    return `El ${this.privateSaleReturnFormatted}% aplica únicamente para inversiones realizadas durante el período de Pre-venta.`;
  }

  percentageToPrivateSale: number = 0;

  private get firstProperty(): any {
    const props = this.info?.properties;
    return Array.isArray(props) && props.length > 0 ? props[0] : null;
  }

  get displayStreet(): string {
    if (this.info?.street) return this.info.street;
    const p = this.firstProperty;
    return p?.location?.street || p?.street || '';
  }

  get displayLocation(): string {
    if (this.info?.location && typeof this.info.location === 'string') return this.info.location;
    if (this.info?.city) return this.info.city;
    const p = this.firstProperty;
    return p?.location?.city || p?.city || '';
  }

  get isFavorite(): boolean {
    return this.info?.id ? this.favoritesSrv.isFavorite(this.info.id) : false;
  }

  async toggleFavorite(event: Event) {
    event.stopPropagation();

    if (!this.logged || !this.userId) {
      this.authSrv.openLoginForm();
      return;
    }

    if (this.info?.id) {
      await this.favoritesSrv.toggleFavorite(this.userId, this.info.id);
    }
  }

  constructor(
    private authSrv: AuthService,
    private kycSrv: KycService,
    private afs: AngularFirestore,
    private crf: ChangeDetectorRef,
    private modalKycSrv: ModalKycSrv,
    private router: Router,
    private favoritesSrv: FavoritesService
  ) {
    this.subs.add(this.authSrv.loggedIn.subscribe((res: boolean) => {
      this.logged = res;
    }));
    this.subs.add(this.authSrv.userAuth.subscribe((user: any) => {
      this.userId = user?.uid || null;
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async ngOnInit() {    //console.log('Info del proyecto:', this.info);
    this.percentageToPrivateSale = this.info?.percentageToPrivateSale ?? 0;

    setInterval(() => {
      this.nowTimeStamp = Math.floor(Date.now() / 1000);
      this.crf.detectChanges();
    }, 3000);
  }

  // NUEVO MÉTODO
  canInvest(): boolean {
    // Solo validaciones básicas de UI
    if (!this.logged || this.loadingButtons || !this.info) {
      return false;
    }
    return true;
  }



  transformDecimals(num: number) {
    const numberFormated = new Intl.NumberFormat('de-DE').format(num);
    return numberFormated;
  }

  // Method to check if the state is "Managing Reinvestments" and should display only this status
  isManagingReinvestments(): boolean {
    const managing = this.info?.managingReinvestments ?? this.info?.managingInvestments;
    return (
      this.info?.proyectStatus === ProjectStatus.ACTIVE &&
      managing &&
      !(this.eurosSold >= this.info.amountToSell * (this.info.priceToken / 100))
    );
  }

  getUrlMainImage() {
    return `url('${this.info.mainImage}')`;
  }

  /**
   * Resuelve la fecha de lanzamiento del proyecto buscando en los distintos
   * campos donde se guarda (nuevo proyecto / set data). Soporta Firestore
   * Timestamp, Date, string ISO o "yyyy-mm-dd".
   */
  private resolveLaunchDate(): Date | null {
    const firstProp = this.firstProperty;
    const raw =
      this.info?.launchDate ??
      this.info?.fechaLanzamiento ??
      this.info?.keyDates?.launchDate ??
      this.info?.keyDates?.fechaLanzamiento ??
      firstProp?.launchDate ??
      firstProp?.keyDates?.launchDate ??
      firstProp?.keyDates?.fechaLanzamiento ??
      this.info?.activationDate ??
      null;

    if (!raw) {
      return null;
    }

    // Firestore Timestamp
    if (typeof raw?.toDate === 'function') {
      return raw.toDate();
    }
    if (typeof raw?.seconds === 'number') {
      return new Date(raw.seconds * 1000);
    }

    const date = new Date(raw);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Cuenta atrás hasta la fecha de lanzamiento para proyectos "En estudio".
   * Devuelve null si no hay fecha o si ya pasó (no se muestra el contador).
   */
  get launchCountdown(): { days: number; hours: number; minutes: number } | null {
    if (this.info?.proyectStatus !== 'En estudio') {
      return null;
    }

    const launchDate = this.resolveLaunchDate();
    if (!launchDate) {
      return null;
    }

    const diffMs = launchDate.getTime() - this.nowTimeStamp * 1000;
    const totalMinutes = Math.floor(diffMs / 60000);
    // Ocultar cuando no queda ni un minuto (evita mostrar 00/00/00).
    if (totalMinutes <= 0) {
      return null;
    }

    const days = Math.floor(totalMinutes / (60 * 24));
    const remainderMinutes = totalMinutes % (60 * 24);
    const hours = Math.floor(remainderMinutes / 60);
    const minutes = remainderMinutes % 60;

    return { days, hours, minutes };
  }

  countdownDayLabel(days: number): string {
    return days === 1 ? 'Día' : 'Días';
  }

  countdownHourLabel(hours: number): string {
    return hours === 1 ? 'Hora' : 'Horas';
  }

  countdownMinuteLabel(minutes: number): string {
    return minutes === 1 ? 'Minuto' : 'Minutos';
  }

  /** Formatea un número con dos dígitos (7 -> "07"). */
  padTime(num: number): string {
    return num.toString().padStart(2, '0');
  }

  toFloor(num: number) {
    if (num === undefined || num === null || num === 0) {
      return 'No activo';
    }

    const secondsDiff = num - this.nowTimeStamp;
    const minutesDiff = secondsDiff / 60;
    const hoursDiff = minutesDiff / 60;
    const daysDiff = hoursDiff / 24;

    if (daysDiff >= 1) {
      return 'Quedan: ' + Math.floor(daysDiff) + ' días';
    }

    if (hoursDiff >= 1) {
      return 'Quedan: ' + Math.floor(hoursDiff) + ' horas';
    }

    if (minutesDiff >= 1) {
      return 'Quedan: ' + Math.floor(minutesDiff) + ' minutos';
    }

    if (secondsDiff >= 1) {
      return 'Quedan: ' + Math.floor(secondsDiff) + ' segundos';
    }

    return 'Tiempo agotado';
  }

  convertToAmount(number: any, factor: any) {
    return number * factor;
  }

  kycNoCompleted() {
    Swal.fire(
      'Error',
      'Debes completar la verificacion de tu cuenta para realizar esta operación.',
      'error'
    );
    this.router.navigate(['/perfil']);
    this.modalKycSrv.openModalKyc();
  }

  openFullScreenModal() {
    const isMultiProperty =
      (Array.isArray(this.info?.properties) && this.info.properties.length > 1) ||
      (Array.isArray(this.info?.viviendas) && this.info.viviendas.length > 1) ||
      this.info?.isMultipleVivienda === true;

    if (isMultiProperty) {
      this.router.navigate(['/propiedades', this.info.id]);
    } else {
      this.router.navigate(['/oportunidad', this.info.id]);
    }
  }

  goToInvestment() {
    console.log(this.info);

    // Validaciones de estado con mensajes específicos
    if (this.info.proyectStatus === 'Vendido') {
      Swal.fire(
        'Proyecto vendido',
        'Este proyecto ya se ha vendido y no está disponible para inversión.',
        'info'
      );
      return;
    }

    if (this.info.proyectStatus === 'Repartiendo dividendos') {
      Swal.fire(
        'Repartiendo dividendos',
        'Este proyecto está en fase de reparto de dividendos y no permite nuevas inversiones.',
        'info'
      );
      return;
    }

    if (this.info.proyectStatus !== 'Activo') {
      Swal.fire(
        'Proyecto no activo',
        'El proyecto no está disponible para inversión en este momento.',
        'info'
      );
      return;
    }

    // Verificar blockchain
    if (!this.info?.blockchain) {
      Swal.fire(
        'Blockchain no disponible',
        'Este proyecto no está disponible para inversión en este momento',
        'info'
      );
      return;
    }

    // Verificar participaciones vendidas
    const targetAmount = this.info.amountToSell * (this.info.priceToken / 100);
    if (this.eurosSold >= targetAmount) {
      Swal.fire(
        'Proyecto financiado',
        'Este proyecto ya ha alcanzado su objetivo de financiación',
        'info'
      );
      return;
    }

    // Verificar tiempo de venta
    if (this.toFloor(this.info.endOfSale) === 'Tiempo agotado') {
      Swal.fire(
        'Tiempo agotado',
        'El período de inversión para este proyecto ha finalizado',
        'info'
      );
      return;
    }

    // Verificar gestión de inversiones
    if (this.info?.managingReinvestments ?? this.info?.managingInvestments) {
      Swal.fire(
        ProjectStatus.MANAGING_REINVESTMENTS,
        'Estamos gestionando sus reinversiones. En breve abriremos el proyecto.',
        'info'
      );
      return;
    }

    // Verificar venta privada
    if (this.info?.onlyInversors) {
      if (!this.userInversor) {
        Swal.fire({
          title: 'Pre-venta',
          html: `<p>Esta oportunidad se encuentra en pre-venta. Para acceder debes cumplir al menos uno de los siguientes requisitos:</p>
          <ul style="text-align: left;"><li>Haber invertido 50.000€ o más con Domoblock.</li>
          <br>
          <li>Haber participado en 5 o más proyectos, con una inversión acumulada mínima de 2.500€.</li></ul>`,
          icon: 'info',
        })
        return;
      }

      if (this.percentageSoldNumber >= this.percentageToPrivateSale) {
        Swal.fire(
          'Pre-venta finalizada',
          'La etapa de pre-venta ha terminado. Espera la apertura de la suscripción general.',
          'info'
        );
        return;
      } else {
        Swal.fire(
          'Pre-venta activa',
          'Puedes invertir en pre-venta.',
          'success'
        );
        this.router.navigate(['/oportunidad'], {
          queryParams: { id: this.info.id },
        });
        return;
      }
    }

    // verificar si el proyecto tiene un formulario externo
    const externalFormInfo = externalInvestForms.find(
      (form) =>
        form.project === this.info.title ||
        form.project === this.info.nameTokens ||
        form.project === this.info.symbolTokens
    );
    if(externalFormInfo) {
      window.open(externalFormInfo.url, '_blank');
      return;
    }

    // Navegación final
    this.router.navigate(['/oportunidad'], {
      queryParams: { id: this.info.id },
    });
  }
}
